import React, { useContext } from 'react';
import {
  Box,
  Grid,
  type Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useI18nContext } from '@procore/core-react';
import { type Project } from '../../types/project';
import { capitalizeFirstLetterOfWords } from '../../helpers/capitalize-first-letter-of-words';
import { hasValue } from '../../helpers/build-texts';
import { useStyles } from './PublicProjectAbout.styles';
import AppContext from '../AppContext/AppContext';
import LabelWithIcon from './LabelWithIcon';
import PublicProjectDataPoint from './PublicProjectDataPoint';
import { TradePill } from './TradePill';
import PublicProjectCover from './PublicProjectCover';
import LocationIcon from '../Icons/LocationIcon';
import BuildingIcon from '../Icons/BuildingIcon';
import CalendarIcon from '../Icons/CalendarIcon';
import BillsIcon from '../Icons/BillsIcon';
import ProfileBannerIcon from '../Icons/ProfileBannerIcon';
import WrenchHammerIcon from '../Icons/WrenchHammerIcon';

const PublicProjectAbout = ({ project }: { project: Project }) => {
  const I18n = useI18nContext();
  const { isUserLoggedIn } = useContext(AppContext);
  const inMobileMode = useMediaQuery(
    (theme: Theme) => theme.breakpoints.down(600),
    { noSsr: true }
  );
  const classes = useStyles();

  const location = project.location;
  const cityStateZipParts: string[] = [];
  if (hasValue(location.city)) cityStateZipParts.push(location.city);
  if (hasValue(location.stateCode)) {
    cityStateZipParts.push(
      hasValue(location.zip)
        ? `${location.stateCode} ${location.zip}`
        : location.stateCode
    );
  }
  const cityStateZip = cityStateZipParts.join(', ');

  const addressText =
    hasValue(location.address) || cityStateZip
      ? [
          hasValue(location.address)
            ? location.address + (inMobileMode ? ' ' : '\n')
            : '',
          cityStateZip,
        ]
          .filter(Boolean)
          .join(inMobileMode ? ' ' : '')
      : location.countryCode === 'US' || location.countryCode === 'CA'
      ? I18n.t(
          'pages.j.public_project_layout.public_project_about.address_to_be_determined_country.' +
            location.countryCode
        )
      : I18n.t(
          'pages.j.public_project_layout.public_project_about.address_to_be_determined_country.fallback'
        );
  const classificationsCount = project.businessClassifications?.length || 0;
  let classificationsString = '';
  for (let i = 0; i < project.businessClassifications.length; i++) {
    if (i !== 0) {
      classificationsString = classificationsString + ', ';
    }
    classificationsString =
      classificationsString + project.businessClassifications[i].name;
    if (project.businessClassifications[i].abbreviation) {
      classificationsString =
        classificationsString +
        ' (' +
        project.businessClassifications[i].abbreviation +
        ')';
    }
  }
  const tradesCount = project?.tradesAndServices?.length || 0;

  return (
    <Box
      className={classes.container}
      data-test-id="public-project-about-contents"
    >
      <div>
        <Typography variant="h2" className={classes.title}>
          {I18n.t(
            'pages.j.public_project_layout.public_project_about.project_details'
          )}
        </Typography>
        <Grid className={!isUserLoggedIn ? classes.hidden : ''} container>
          <Grid
            className={classes.dataPointContainer}
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
          >
            <PublicProjectDataPoint
              label={I18n.t(
                'pages.j.public_project_layout.public_project_about.address'
              )}
              icon={<LocationIcon viewBox="0 -1.5 22 22" />}
              text={addressText}
            />
          </Grid>
          {project.projectSectorName && (
            <Grid
              className={classes.dataPointContainer}
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
            >
              <PublicProjectDataPoint
                label={I18n.t(
                  'pages.j.public_project_layout.public_project_about.market_sector'
                )}
                icon={<BuildingIcon viewBox="0 -1 28 28" />}
                text={capitalizeFirstLetterOfWords(project.projectSectorName)}
              />
            </Grid>
          )}
          {project.estimatedTimeline && (
            <Grid
              className={classes.dataPointContainer}
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
            >
              <PublicProjectDataPoint
                label={I18n.t(
                  'pages.j.public_project_layout.public_project_about.estimated_timeline'
                )}
                icon={<CalendarIcon viewBox="0 -1.5 22 22" />}
                text={project.estimatedTimeline}
              />
            </Grid>
          )}
          {project.fundingType && (
            <Grid
              className={classes.dataPointContainer}
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
            >
              <PublicProjectDataPoint
                label={I18n.t(
                  'pages.j.public_project_layout.public_project_about.funding_type'
                )}
                icon={<BillsIcon viewBox="0 -1.5 20 16" />}
                text={capitalizeFirstLetterOfWords(project.fundingType)}
              />
            </Grid>
          )}
          {classificationsCount > 0 && (
            <Grid
              className={classes.dataPointContainer}
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
            >
              <PublicProjectDataPoint
                label={I18n.t(
                  'pages.j.public_project_layout.public_project_about.business_classifications'
                )}
                icon={<ProfileBannerIcon viewBox="-2 -4 20 20" />}
                text={capitalizeFirstLetterOfWords(classificationsString)}
              />
            </Grid>
          )}
          {tradesCount > 0 && (
            <Grid
              className={classes.dataPointContainer}
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
            >
              <LabelWithIcon
                label={I18n.t(
                  'pages.j.public_project_layout.public_project_about.trades_and_services'
                )}
                icon={<WrenchHammerIcon viewBox="0 -4 30 32" />}
              />
              <div className={classes.trades}>
                {project.tradesAndServices.map((trade) => (
                  <TradePill
                    name={capitalizeFirstLetterOfWords(trade.key)}
                    key={trade.key}
                  />
                ))}
              </div>
            </Grid>
          )}
        </Grid>
        {!isUserLoggedIn && <PublicProjectCover />}
      </div>
    </Box>
  );
};

export default PublicProjectAbout;
